import type { ITrackEvent } from '~/routes/api.track-event.query/route';
import type { Campaign } from '~/modules/campaign/types';

import {
  findVariantByOrigin,
  getSearchParamByCampaign,
  useCampaignVariantPreview,
} from '~/modules/campaign';
import { useTrackingEventMutation } from '~/modules/tracking/api/useTrackingSlackMutation';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { useFeatureFlagChecker } from '~/modules/apps';
import { getConfig } from '~/config/environment';
import { parseTemplateInfoBy } from '~/utils';
import { getShopifyConfig } from '~/config';

const GP_V7_API_URL = getConfig('URL')?.GP_V7_API_URL;
const URL_VALID = 'api.gemcommerce.com';
export const useTrackingSlack = () => {
  const { getLinkPreview } = useCampaignVariantPreview();
  const { isGemUser } = useFeatureFlagChecker();

  const trackingEventMutation = useTrackingEventMutation();

  const onHandleTracking = async (data: ITrackEvent['data']) => {
    const shop = getShopifyConfig()?.shopifyDomain ?? '';
    await trackingEventMutation.mutateAsync({
      inputs: {
        data: {
          shop,
          ...data,
        },
      },
    });
  };

  const handleTrackingCampaign = async (campaign?: Campaign) => {
    if (isGemUser) return;

    if (!GP_V7_API_URL?.includes(URL_VALID)) return;

    if (!campaign || !campaign.status) return;

    const arrayStatus = [CAMPAIGN_STATUS.RUNNING, CAMPAIGN_STATUS.PAUSED, CAMPAIGN_STATUS.STOPPED];
    if (!arrayStatus.includes(campaign.status)) return;

    const originVariant = findVariantByOrigin(campaign, true);
    if (!originVariant) return;
    if (!originVariant.trafficRate) return;

    const templateInfo = parseTemplateInfoBy({ templateSuffix: originVariant.templateSuffix });
    if (!templateInfo) return;

    const linkLivePage = await getLinkPreview(templateInfo);
    const searchParams = getSearchParamByCampaign(campaign, { active: 'variantA' });

    let url: URL | undefined;

    try {
      url = new URL(linkLivePage);
      url.search = searchParams;
    } catch (error) {}

    const trafficRate = originVariant.trafficRate;
    const traffic = `${trafficRate * 100}/${100 - trafficRate * 100}`;
    const targetMarkets = campaign.conditions?.targetMarkets ?? [];
    const markets = targetMarkets
      .map((market) => {
        try {
          const url = new URL(market);
          return url.pathname.replace('/', '');
        } catch (error) {}
      })
      .filter(Boolean);

    const data: ITrackEvent['data'] = {
      ID: campaign.id,
      Status: campaign.status,
      Goal: campaign.priority,
      Device: campaign.targetDevices?.join(', ') ?? 'all',
      Channel: campaign.targetChannels?.join(', ') ?? 'all',
      Visitor: campaign.targetVisitorTypes?.join(', ') ?? 'all',
      Market: markets.join(', ') ?? 'all',
      Traffic: traffic,
      Preview: `<${url?.href}|Link [Click here]>`,
    };

    onHandleTracking(data);
  };

  return {
    handleTrackingCampaign,
  };
};
